import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { MaskedTextInput, MaskedTextInputProps } from '../MaskedTextInput/MaskedTextInput';
import { TextAreaDecorator, TextAreaDecoratorProps } from '../TextAreaDecorator/TextAreaDecorator';
import { TextInputDecorator, TextInputDecoratorProps } from '../TextInputDecorator/TextInputDecorator';
import { ScopedTooltip, TooltipDecoratorProps } from '../TooltipDecorator/ScopedTooltip';

import './InputField.css';

export enum InputFieldType {
    TEXT = 'text',
    TEXTAREA = 'textarea',
    MASKED_TEXT = 'masked-text'
}

export interface InputFieldTextProps extends TextInputDecoratorProps, InputFieldCommonProps {
    inputType: InputFieldType.TEXT;
}

export interface InputFieldTextAreaProps extends TextAreaDecoratorProps, InputFieldCommonProps {
    inputType: InputFieldType.TEXTAREA;
}

export interface InputFieldMaskedTextProps extends MaskedTextInputProps, InputFieldCommonProps {
    inputType: InputFieldType.MASKED_TEXT;
}

interface InputFieldCommonProps {
    errorText?: React.ReactNode;
    hintText?: React.ReactNode;
    hintTooltip?: Partial<TooltipDecoratorProps>;
    errorTooltip?: Partial<TooltipDecoratorProps>;
    notShowTooltip?: boolean;
}

export type InputFieldProps = InputFieldTextProps | InputFieldTextAreaProps | InputFieldMaskedTextProps;

export class InputField extends React.PureComponent<InputFieldProps> {
    static HINT_TOOLTIP_DEFAULT_PROPS: Partial<TooltipDecoratorProps> = {
        view: 'default',
        tone: 'default',
        to: 'bottom',
        size: 'n'
    };

    static ERROR_TOOLTIP_DEFAULT_PROPS: Partial<TooltipDecoratorProps> = {
        view: 'classic',
        tone: 'default',
        to: 'right',
        size: 'n'
    };

    render() {
        const { cls } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls({
                        block: 'input-field'
                    }),
                    cls
                )}
            >
                {this.getInputContent()}
                {this.getTooltip()}
                {this.getErrorTooltip()}
            </div>
        );
    }

    getInputContent() {
        switch (this.props.inputType) {
            case InputFieldType.TEXT: {
                const { cls, inputType, errorText, hintText, ...inputProps } = this.props;

                return <TextInputDecorator {...inputProps} />;
            }

            case InputFieldType.MASKED_TEXT: {
                const { cls, inputType, errorText, hintText, ...inputProps } = this.props;

                return <MaskedTextInput {...inputProps} />;
            }

            case InputFieldType.TEXTAREA: {
                const { cls, inputType, errorText, hintText, ...inputProps } = this.props;

                return <TextAreaDecorator {...inputProps} />;
            }

            default:
                throw new Error('unknown InputFieldType');
        }
    }

    private getTooltip() {
        const { hintText, hintTooltip, focused, notShowTooltip } = this.props;

        if (!hintText || notShowTooltip) {
            return null;
        }

        const hintTooltipProps = {
            ...InputField.HINT_TOOLTIP_DEFAULT_PROPS,
            ...hintTooltip
        };

        return (
            <ScopedTooltip
                {...hintTooltipProps}
                cls={Bem.cls('input-field', 'tooltip')}
                anchor={this}
                theme={hintTooltipProps.theme || 'promo'}
                visible={focused}
            >
                <div className={Bem.cls('input-field', 'tooltip-content')}>{hintText}</div>
            </ScopedTooltip>
        );
    }

    private getErrorTooltip() {
        const { errorText, errorTooltip, focused, notShowTooltip } = this.props;

        if (!errorText || notShowTooltip) {
            return null;
        }

        const errorTooltipProps = {
            ...InputField.ERROR_TOOLTIP_DEFAULT_PROPS,
            ...errorTooltip
        };

        return (
            <ScopedTooltip
                {...errorTooltipProps}
                cls={Bem.cls('input-field', 'tooltip')}
                anchor={this}
                theme="error"
                visible={focused}
            >
                <div className={Bem.cls('input-field', 'error-tooltip-content')}>{errorText}</div>
            </ScopedTooltip>
        );
    }
}
